import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Chip } from "../../components/ui/chip";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Badge } from "../../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { NoticeOfAssignment, ScheduleLine } from "../../types/schema";
import {
  FileSpreadsheet,
  Building2,
  Layers,
  CheckCircle2,
  MinusCircle,
  CircleDashed,
  Download,
  Loader2,
} from "lucide-react";
import ExcelJS from "exceljs";
import { useState } from "react";
import { toast } from "react-toastify";
import apiService from "../../services/apiService";

interface ScheduleLike {
  lines?: ScheduleLine[];
}

type AssignedStatus = "A" | "P" | "N";

type ScheduleLineWithAssignment = ScheduleLine & {
  astrafinRetained?: number;
  lenderShare?: number;
  assignedStatus?: AssignedStatus | null;
  /** @deprecated legacy field — prefer assignedStatus */
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  noa: NoticeOfAssignment;
  schedule: ScheduleLike | undefined;
}

const fundingModeLabel = (mode: string) => {
  switch (mode) {
    case "A":
      return "Pre GST — Base Only";
    case "B":
      return "Post GST — Base + GST";
    case "C":
      return "Post TDS — Base − TDS + GST";
    default:
      return mode;
  }
};

const inr = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const statusLabel = (s: AssignedStatus) => {
  if (s === "A") return "Fully assigned";
  if (s === "P") return "Partial assigned";
  return "Not assigned";
};

const normalizeAssignmentStatus = (value: unknown): AssignedStatus => {
  const raw = String(value ?? "").trim().toUpperCase();
  // A = fully assigned; accept legacy "F" from older records
  if (raw === "A" || raw === "F") return "A";
  if (raw === "P") return "P";
  if (raw === "N") return "N";
  return "N"; // null / undefined / anything else → Not assigned
};

export default function NoAVisualisePreviewModal({ open, onOpenChange, noa, schedule }: Props) {
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const lines = schedule?.lines ?? [];
  const partials = noa.partialSplits ?? {};

  const rows = lines.map((line) => {
    const l = line as ScheduleLineWithAssignment;
    const gst = l.gstRaw;
    const base = l.baseRentRounded ?? 0;
    const total = base + gst;
    const partial = partials[l.scheduleLineId];
    const lenderShare = Number(l.lenderShare ?? partial?.lenderShare ?? 0);
    const status = normalizeAssignmentStatus(
      l.assignedStatus ?? partial?.assignedStatus
    );
    const rawRetained = Number(l.astrafinRetained ?? partial?.astrafinRetained ?? 0);
    // Not assigned (N): Astrafin retained = base amount
    const astrafinRetained = status === "N" ? base : rawRetained;

    return {
      lineId: l.scheduleLineId,
      periodNo: l.periodNo,
      isInterim: l.isInterim,
      fromDate: l.fromDate,
      toDate: l.toDate,
      dueDate: l.dueDate,
      base,
      gst,
      total,
      status,
      assignedAmount: lenderShare,
      astrafinRetained,
    };
  });

  const totals = rows.reduce(
    (acc, r) => {
      acc.base += r.base;
      acc.gst += r.gst;
      acc.total += r.total;
      acc.assigned += r.assignedAmount;
      // retained already uses base when status === "N"
      acc.retained += r.astrafinRetained;
      if (r.status === "A") acc.a += 1;
      else if (r.status === "P") acc.p += 1;
      else acc.n += 1;
      return acc;
    },
    { base: 0, gst: 0, total: 0, assigned: 0, retained: 0, a: 0, p: 0, n: 0 }
  );

  const statusChip = (s: AssignedStatus) => {
    if (s === "A") {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">A</Badge>
      );
    }
    if (s === "P") {
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">P</Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-muted-foreground">N</Badge>
    );
  };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("NoA Assignment Preview");

    worksheet.columns = [
      { header: "Period", key: "period", width: 12 },
      { header: "From Date", key: "fromDate", width: 14 },
      { header: "To Date", key: "toDate", width: 14 },
      { header: "Due Date", key: "dueDate", width: 14 },
      { header: "Base Rent", key: "base", width: 14 },
      { header: "GST", key: "gst", width: 14 },
      { header: "Total", key: "total", width: 14 },
      { header: "Assigned (A/P/N)", key: "status", width: 16 },
      { header: "Assigned Amount", key: "assigned", width: 18 },
      { header: "Astrafin Retained", key: "retained", width: 18 },
    ];

    rows.forEach((r) => {
      worksheet.addRow({
        period: `${r.periodNo}${r.isInterim ? " (Interim)" : ""}`,
        fromDate: r.fromDate,
        toDate: r.toDate,
        dueDate: r.dueDate,
        base: Math.round(r.base),
        gst: r.gst,
        total: r.total,
        status: r.status,
        assigned: r.assignedAmount,
        retained: r.astrafinRetained,
      });
    });

    worksheet.addRow({
      period: "TOTALS",
      fromDate: "",
      toDate: "",
      dueDate: "",
      base: Math.round(totals.base),
      gst: totals.gst,
      total: totals.total,
      status: `A:${totals.a} P:${totals.p} N:${totals.n}`,
      assigned: totals.assigned,
      retained: totals.retained,
    });

    worksheet.addRow({});
    worksheet.addRow({ period: "NoA ID", fromDate: noa.noaId });
    worksheet.addRow({ period: "Schedule ID", fromDate: noa.scheduleId });
    worksheet.addRow({ period: "Lender", fromDate: noa.lender });
    worksheet.addRow({
      period: "Funding Mode",
      fromDate: `${noa.fundingMode} — ${fundingModeLabel(noa.fundingMode)}`,
    });
    worksheet.addRow({ period: "PV Total", fromDate: Math.round(noa.pvTotal) });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    const totalRow = worksheet.getRow(rows.length + 2);
    totalRow.font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `NoA-${noa.noaId}-assignment-preview.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadDocx = async () => {
    if (isDownloadingDocx) return;
    if (!noa.noa_template) {
      toast.error("NOA template is not available");
      return;
    }
    if (!noa.id) {
      toast.error("NOA ID is missing");
      return;
    }

    setIsDownloadingDocx(true);
    try {
      const response = await apiService.post<Blob>(
        "/document/download_dynamic_docx",
        {
          noa_id: noa.id,
          noa_template: noa.noa_template,
        },
        { responseType: "blob" }
      );

      const blob = response as Blob;
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${noa.noaId || `NOA-${noa.id}`}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success("Downloaded successfully");
    } catch {
      toast.error("Failed to download document");
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col md:max-w-6xl">
        <DialogHeader>
          <DialogTitle>NoA Assignment Preview — {noa.noaId}</DialogTitle>
          <DialogDescription>
            Period-wise view of the linked rental schedule against this Notice of Assignment.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <Chip variant="primary" className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              Lender: {noa.lender}
            </Chip>
            <Chip variant="info">
              Funding Mode: {noa.fundingMode} — {fundingModeLabel(noa.fundingMode)}
            </Chip>
            <Chip variant="secondary" className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              Total Periods: {rows.length}
            </Chip>
            <Chip variant="success" className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Fully Assigned (A): {totals.a}
            </Chip>
            <Chip variant="warning" className="flex items-center gap-1">
              <CircleDashed className="h-3 w-3" />
              Partial (P): {totals.p}
            </Chip>
            <Chip variant="secondary" className="flex items-center gap-1">
              <MinusCircle className="h-3 w-3" />
              Not Assigned (N): {totals.n}
            </Chip>
            <Chip variant="primary">Total Assigned: {inr(totals.assigned)}</Chip>
          </div>

          <div className="rounded-md border">
            <div className="flex justify-between items-center p-4 border-b gap-2">
              <h3 className="text-sm font-semibold">Assignment Preview</h3>
              <div className="flex items-center gap-2">
                {noa.noa_template && (
                  <Button
                    onClick={downloadDocx}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    disabled={isDownloadingDocx}
                  >
                    {isDownloadingDocx ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    {isDownloadingDocx ? "Downloading..." : "Download DOCX"}
                  </Button>
                )}
                <Button
                  onClick={downloadExcel}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Download Excel
                </Button>
              </div>
            </div>
            <ScrollArea className="h-[440px]">
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Assigned Amount</TableHead>
                      <TableHead className="text-right">Astrafin Retained</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                          {schedule ? "No schedule lines found." : "Linked schedule not available."}
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((r) => (
                      <TableRow
                        key={r.lineId}
                        className={
                          r.status === "N"
                            ? "opacity-60"
                            : r.status === "P"
                              ? "border-l-2 border-l-amber-400"
                              : ""
                        }
                      >
                        <TableCell>
                          {r.periodNo}
                          {r.isInterim ? " (Interim)" : ""}
                        </TableCell>
                        <TableCell>{r.fromDate}</TableCell>
                        <TableCell>{r.toDate}</TableCell>
                        <TableCell>{r.dueDate}</TableCell>
                        <TableCell className="text-right">{inr(r.base)}</TableCell>
                        <TableCell className="text-right">{inr(r.gst)}</TableCell>
                        <TableCell className="text-right">{inr(r.total)}</TableCell>
                        <TableCell className="text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{statusChip(r.status)}</span>
                            </TooltipTrigger>
                            <TooltipContent>{statusLabel(r.status)}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {inr(r.assignedAmount)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {inr(r.status === "N" ? r.base : r.astrafinRetained)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length > 0 && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={4} className="text-right font-medium">
                          Totals
                        </TableCell>
                        <TableCell className="text-right font-medium">{inr(totals.base)}</TableCell>
                        <TableCell className="text-right font-medium">{inr(totals.gst)}</TableCell>
                        <TableCell className="text-right font-medium">{inr(totals.total)}</TableCell>
                        <TableCell className="text-center text-xs text-muted-foreground">
                          A:{totals.a} · P:{totals.p} · N:{totals.n}
                        </TableCell>
                        <TableCell className="text-right font-semibold">{inr(totals.assigned)}</TableCell>
                        <TableCell className="text-right font-semibold">{inr(totals.retained)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </ScrollArea>
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            A = Fully assigned (full lender share). P = Partial assigned (Astrafin retains some amount). N = Not
            assigned (including null/missing status). Assigned Amount = lenderShare; Astrafin Retained =
            astrafinRetained. Reconciles with NoA PV Total of {inr(noa.pvTotal)} on a present-value basis.
          </p>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
